/**
 * Sentinel-append pagination: reveal `pageSize` more items whenever an
 * off-the-end sentinel scrolls into view. Combined with
 * `content-visibility: auto` on tiles this keeps the wall cheap well past a
 * few hundred items without any list virtualization.
 *
 * VIRTUALIZATION SEAM: above ~`VIRTUALIZATION_THRESHOLD` revealed tiles the
 * DOM itself becomes the cost. The intended next step is row virtualization
 * of the *layout container* (grid rows / masonry column slices), keyed off
 * `visibleCount`, not a third-party list — `rc-virtual-list` is only a
 * transitive antd dependency and must not be imported directly.
 */
import { type RefObject, useCallback, useEffect, useRef, useState } from "react"

export const VIRTUALIZATION_THRESHOLD = 500

/** One viewport of lookahead so the append lands before the user reaches it. */
export const SENTINEL_ROOT_MARGIN = "100%"

export interface SentinelPagination {
  visibleCount: number
  hasMore: boolean
  /** Attach to an element placed after the last rendered tile. */
  sentinelRef: (node: HTMLElement | null) => void
  /** Manual fallback (e.g. a "Show more" button when IO is unavailable). */
  revealMore: () => void
}

/**
 * `rootRef` must be the element that actually scrolls the tiles (the wall's
 * stage). Observing against the viewport instead is wrong whenever the wall
 * sits inside another scroll container: the browser clips the sentinel by
 * every ancestor between it and the root, so a wall whose bottom edge is below
 * the outer container's fold never sees its sentinel intersect, and page two
 * never appends even though the user scrolled the wall itself to the end.
 * Ancestors ABOVE the root are ignored, so rooting on the stage sidesteps that.
 */
export const useSentinelPagination = (
  total: number,
  pageSize: number,
  rootRef?: RefObject<HTMLElement | null>
): SentinelPagination => {
  const [revealed, setRevealed] = useState(pageSize)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const nodeRef = useRef<HTMLElement | null>(null)

  const visibleCount = Math.min(revealed, total)
  const hasMore = visibleCount < total

  const revealMore = useCallback(() => {
    setRevealed((prev) => Math.min(prev + pageSize, Math.max(total, pageSize)))
  }, [pageSize, total])

  const observe = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null
      nodeRef.current = node
      if (!node || typeof IntersectionObserver === "undefined") return
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) revealMore()
        },
        { root: rootRef?.current ?? null, rootMargin: SENTINEL_ROOT_MARGIN }
      )
      observer.observe(node)
      observerRef.current = observer
    },
    [revealMore, rootRef]
  )

  // Re-arm when the reveal callback changes (total / pageSize moved) so a
  // still-visible sentinel keeps paging. This also runs after the first
  // commit, when `rootRef.current` is guaranteed to be attached — the sentinel
  // is a descendant of the root, so React runs its callback ref before the
  // root's ref is set and that first observer may have been rooted on the
  // viewport.
  useEffect(() => {
    observe(nodeRef.current)
    return () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    }
  }, [observe])

  return { visibleCount, hasMore, sentinelRef: observe, revealMore }
}
